"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { DolibarrClient, getDolibarrClient, loginToDolibarr } from "@/lib/dolibarr/client";

interface User {
  id: string;
  name: string;
  email: string;
  admin: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  user: User | null;
  client: DolibarrClient | null;
  login: (apiKey: string) => Promise<boolean>;
  loginWithCredentials: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const PUBLIC_PATHS = ["/login"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [client, setClient] = useState<DolibarrClient | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    const storedKey = localStorage.getItem("dolapikey");

    if (!storedKey) {
      setIsAuthenticated(false);
      setUser(null);
      setClient(null);
      setIsLoading(false);
      return;
    }

    const doliClient = getDolibarrClient(storedKey);

    if (!doliClient) {
      setIsAuthenticated(false);
      setUser(null);
      setClient(null);
      setIsLoading(false);
      return;
    }

    try {
      // Valide la clé auprès de Dolibarr et (r)établit le cookie de session
      // httpOnly côté serveur (indispensable au middleware d'authentification).
      const res = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: storedKey }),
      });
      if (res.ok) {
        const { user } = await res.json();
        setIsAuthenticated(true);
        setUser(user);
        setClient(doliClient);
      } else {
        localStorage.removeItem("dolapikey");
        setIsAuthenticated(false);
        setUser(null);
        setClient(null);
      }
    } catch (err) {
      localStorage.removeItem("dolapikey");
      setIsAuthenticated(false);
      setUser(null);
      setClient(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!isLoading) {
      const isPublicPath = PUBLIC_PATHS.includes(pathname);
      if (!isAuthenticated && !isPublicPath) {
        router.replace("/login");
      } else if (isAuthenticated && isPublicPath) {
        router.replace("/");
      }
    }
  }, [isAuthenticated, isLoading, pathname, router]);

  const login = async (apiKey: string): Promise<boolean> => {
    const doliClient = new DolibarrClient(apiKey);

    try {
      // Valide la clé + pose le cookie de session httpOnly côté serveur.
      const res = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey }),
      });
      if (res.ok) {
        const { user } = await res.json();
        localStorage.setItem("dolapikey", apiKey);
        setIsAuthenticated(true);
        setUser(user);
        setClient(doliClient);
        router.replace("/");
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const loginWithCredentials = async (
    username: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    const result = await loginToDolibarr(username, password);

    if (result.success && result.token) {
      const doliClient = new DolibarrClient(result.token);

      try {
        // Valide la clé + pose le cookie de session httpOnly côté serveur.
        const res = await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apiKey: result.token }),
        });
        if (res.ok) {
          const { user } = await res.json();
          localStorage.setItem("dolapikey", result.token);
          setIsAuthenticated(true);
          setUser(user);
          setClient(doliClient);
          router.replace("/");
          return { success: true };
        }
        return { success: false, error: "Échec de la vérification du compte" };
      } catch (err) {
        return { success: false, error: "Erreur lors de la vérification" };
      }
    }

    return { success: false, error: result.error };
  };

  const logout = () => {
    localStorage.removeItem("dolapikey");
    setIsAuthenticated(false);
    setUser(null);
    setClient(null);
    // Efface le cookie de session côté serveur (non bloquant).
    fetch("/api/auth/session", { method: "DELETE" }).catch(() => {});
    router.replace("/login");
  };

  // Show nothing while checking auth to prevent flash
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, isAdmin: user?.admin ?? false, user, client, login, loginWithCredentials, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
